// ============================================================================
// CostWorksheetPanel — Interne PV-Kosten-Kalkulation (Stage 1)
// ----------------------------------------------------------------------------
// UI-Schicht über calculation-core/pv-cost-calculator (läuft SERVERSEITIG via
// POST /projects/:id/costing/calculate — Architekturregel: Berechnung in der
// Engine, UI rendert nur). Spezifikation: ELMETIC-Angebots-Kalkulator 06/2026.
//
// IMMER sichtbar: ENTWURF · Interne Kalkulation · Kein Angebot ·
// Menschliche Prüfung erforderlich. Stage 1 = transient (kein Persist).
// ============================================================================
import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/lib/api-client.js";
import { eur as fmtEur, num as fmtNum } from "@/lib/format.js";
import { useAuthStore } from "@/stores/auth.store.js";
import { CostPrintSheet } from "./CostPrintSheet.js";

// ─── API-Typen (Spiegel der Engine-Ausgabe) ──────────────────────────────────

interface Quantities {
  moduleCount: number; fastenerCount: number; railMeters: number;
  smallRoofFactor: number; dcWireMeters: number; dcSurgeCount: number;
  groundingWireMeters: number; labourMinutes: number; labourHours: number;
}
interface Position { key: string; label: string; ekEur: number | null; vkEur: number }
interface CostOutput {
  quantities: Quantities;
  materialPositions: Position[];
  labourPosition: Position;
  flatPositions: Position[];
  // EK/Marge/Stundensatz sind für Installateure serverseitig null (redigiert).
  materialEkEur: number | null; materialVkEur: number; labourEkEur: number | null; labourVkEur: number;
  subtotalNetEur: number; surchargeEur: number; totalNetEur: number;
  vatEur: number; totalGrossEur: number;
  effectiveHourlyRateEur: number | null; minutesPerModule: number;
  pricePerKwpNetEur: number; pricePerModuleNetEur: number; marginEur: number | null;
  canApprove: false; requiresHumanReview: true;
}
interface EngineEnvelope {
  status: string;
  data: CostOutput | null;
  warnings: Array<{ code: string; message: string; severity: string }>;
  errors: Array<{ code: string; message: string }>;
  assumptions: Array<{ field: string; assumed: unknown; reason: string; impactOnResult: string }>;
  confidenceScore: number;
}
interface CatalogItem {
  id: string; category: string; name: string; unit: string;
  // Partner (installer) erhalten ekBestEur/bestSupplier/provenance/note NICHT (Server-Whitelist);
  // `selectable` = serverseitig mit EK kalkulierbar.
  ekBestEur?: number | null; vkGuidelineEur: number | null;
  dataQuality: "verified" | "unverified" | "sample";
  bestSupplier?: string | null; provenance?: string; note?: string; selectable?: boolean;
}

interface ExtraRow { itemId: string; quantity: string }

// ─── KB-Komponenten ───────────────────────────────────────────────────────────
interface KbComponent { id: string; type: string; manufacturer: string; modelName: string; specs?: Record<string, unknown> }
// Globaler Fach-Katalog (DB-unabhängig) — Endpunkte /components/wechselrichter | /batterien.
// Liefert immer befüllte Listen (kein Tenant-/Seed-Hang); Felder gem. recommendation.ts.
interface KatalogWr {
  id: string; hersteller: string; modell: string; leistungKw: number;
  preisEur?: number; wirkungsgrad?: number; empfohlenAbKwp?: number; empfohlenBisKwp?: number;
  maxDcLeistungKw?: number; batterieFaehig?: boolean; selectable?: boolean;
}
interface KatalogBatt { id: string; hersteller: string; modell: string; kapazitaetKwh: number; preisEur?: number; selectable?: boolean }

/** Passenden Wechselrichter zur Anlagengröße wählen: kWp im Empfehlungsband, sonst
 *  kleinster WR, dessen max. DC-Leistung die kWp deckt; Tie-Break bester Wirkungsgrad. */
function pickInverterForKwp(list: KatalogWr[], kwp: number): KatalogWr | null {
  if (!(kwp > 0) || list.length === 0) return null;
  const byEff = (a: KatalogWr, b: KatalogWr) => (b.wirkungsgrad ?? 0) - (a.wirkungsgrad ?? 0);
  const inBand = list.filter(
    (w) => w.empfohlenAbKwp != null && w.empfohlenBisKwp != null &&
      kwp >= w.empfohlenAbKwp && kwp <= w.empfohlenBisKwp,
  );
  if (inBand.length > 0) return [...inBand].sort(byEff)[0] ?? null;
  // kein Band-Treffer: kleinster WR, der die DC-Leistung trägt (sonst größter verfügbarer)
  const covering = list
    .filter((w) => (w.maxDcLeistungKw ?? w.leistungKw) >= kwp)
    .sort((a, b) => (a.maxDcLeistungKw ?? a.leistungKw) - (b.maxDcLeistungKw ?? b.leistungKw));
  if (covering.length > 0) return covering[0] ?? null;
  return [...list].sort((a, b) => (b.maxDcLeistungKw ?? b.leistungKw) - (a.maxDcLeistungKw ?? a.leistungKw))[0] ?? null;
}

// Einheitliche €-Formatierung über den geteilten Helfer (Suffix, de-AT), 2 Nachkommastellen.
const eur = (n: number) => fmtEur(n, 2);

const QUALITY_BADGE: Record<CatalogItem["dataQuality"], string> = {
  verified: "✓ Quelle 06/2026",
  unverified: "Richtwert",
  sample: "MUSTER",
};

// ─── Komponente ──────────────────────────────────────────────────────────────

export function CostWorksheetPanel({ projectId }: { projectId?: string }) {
  // Installateur-Partner: nur Endpreise, keine internen EK/Marge/Sätze.
  const isInstaller = useAuthStore((s) => s.user?.role) === "installer";
  // Eingaben (Defaults = Excel)
  const [kwp, setKwp] = useState("");
  const [moduleWp, setModuleWp] = useState("475");
  const [ukSystem, setUkSystem] = useState<"two_rail" | "cross_rail" | "three_rail" | "standing_seam">("two_rail");
  const [roofSurfaces, setRoofSurfaces] = useState("1");
  const [strings, setStrings] = useState("1");
  const [dcPath, setDcPath] = useState("10");
  const [acPath, setAcPath] = useState("5");
  const [distributorMod, setDistributorMod] = useState(true);
  const [meterLoop, setMeterLoop] = useState(false);
  const [travelKm, setTravelKm] = useState("0");
  const [trips, setTrips] = useState("3");
  const [surchargePct, setSurchargePct] = useState("0");
  const [craneCost, setCraneCost] = useState("0");
  const [fallProtectionCost, setFallProtectionCost] = useState("0");
  const [conduitLength, setConduitLength] = useState("20");
  const [conduitBends, setConduitBends] = useState("2");
  // Sätze (anpassbar)
  const [hourlyRate, setHourlyRate] = useState("72");
  const [labourEkRate, setLabourEkRate] = useState("40");
  const [materialMarkup, setMaterialMarkup] = useState("36");
  const [labourMarkup, setLabourMarkup] = useState("0");
  const [moduleEk, setModuleEk] = useState("70");
  const [showRates, setShowRates] = useState(false);
  // KB-Komponenten-Auswahl
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [selectedInverterId, setSelectedInverterId] = useState("");
  const [inverterTouched, setInverterTouched] = useState(false); // true = manuell gewählt → keine Auto-Vorwahl mehr
  const [inverterAuto, setInverterAuto] = useState(false);        // true = aktuell automatisch vorgewählt (UI-Hinweis)
  const [selectedBatteryId, setSelectedBatteryId] = useState("");
  const [kbModules, setKbModules] = useState<KbComponent[]>([]);
  const [kbInverters, setKbInverters] = useState<KatalogWr[]>([]);
  const [kbBatteries, setKbBatteries] = useState<KatalogBatt[]>([]);
  // Preiskatalog
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [extraRows, setExtraRows] = useState<ExtraRow[]>([{ itemId: "", quantity: "1" }]);
  // Ergebnis
  const [result, setResult] = useState<EngineEnvelope | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showPrintSheet, setShowPrintSheet] = useState(false);

  useEffect(() => {
    void apiClient.get<{ items: CatalogItem[] }>("/costing/catalog").then((res) => {
      if (res.success) setCatalog(res.data.items);
    });
  }, []);

  useEffect(() => {
    void apiClient.get<KbComponent[]>("/components?type=pv_module").then((r) => { if (r.success) setKbModules(Array.isArray(r.data) ? r.data : []); });
    // Wechselrichter/Batterie aus dem globalen Fach-Katalog (immer befüllt, DB-unabhängig).
    void apiClient.get<KatalogWr[]>("/components/wechselrichter").then((r) => { if (r.success) setKbInverters(Array.isArray(r.data) ? r.data : []); });
    void apiClient.get<KatalogBatt[]>("/components/batterien").then((r) => { if (r.success) setKbBatteries(Array.isArray(r.data) ? r.data : []); });
  }, []);

  // kWp aus dem neuesten Dachentwurf vorbefüllen — NUR wenn das Feld noch leer ist
  // (eine geladene gespeicherte Kalkulation darf nicht überschrieben werden).
  const [kwpFromRoof, setKwpFromRoof] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    void apiClient
      .get<Array<{ summaryJson?: { result?: { estimatedKwp?: number } } }>>(`/projects/${projectId}/roof-designs`)
      .then((r) => {
        if (!r.success || !Array.isArray(r.data)) return;
        const k = r.data[0]?.summaryJson?.result?.estimatedKwp;
        if (typeof k !== "number" || !(k > 0)) return;
        setKwp((prev) => {
          if (prev.trim() !== "") return prev;
          setKwpFromRoof(true);
          return String(k);
        });
      })
      .catch(() => {});
  }, [projectId]);

  const num = (v: string): number => {
    const n = parseFloat(v.replace(",", "."));
    return isFinite(n) ? n : 0;
  };

  // Wechselrichter automatisch passend zur Anlagengröße vorwählen (bis manuell geändert).
  useEffect(() => {
    if (inverterTouched) return;
    const k = num(kwp);
    if (!(k > 0) || kbInverters.length === 0) return;
    const pick = pickInverterForKwp(kbInverters, k);
    if (pick && pick.id !== selectedInverterId) {
      setSelectedInverterId(pick.id);
      setInverterAuto(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kwp, kbInverters, inverterTouched]);

  const buildCalcBody = useCallback(() => {
    const extraComponents = extraRows
      .map((row) => {
        const item = catalog.find((c) => c.id === row.itemId);
        if (!item || (item.ekBestEur == null && !item.selectable)) return null;
        // ref: Partner-EK wird serverseitig aufgelöst; der Preis hier wird für Partner ignoriert.
        return { name: item.name, unitPriceEkEur: item.ekBestEur ?? 0, ref: `price:${item.id}`, quantity: Math.max(1, Math.round(num(row.quantity))) };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    // Gewählten Wechselrichter (auto oder manuell) + Speicher als Kostenposition einspeisen.
    const wr = kbInverters.find((w) => w.id === selectedInverterId);
    if (wr && (wr.selectable || (wr.preisEur != null && wr.preisEur > 0))) {
      extraComponents.push({ name: `Wechselrichter ${wr.hersteller} ${wr.modell}`, unitPriceEkEur: wr.preisEur ?? 0, ref: `wr:${wr.id}`, quantity: 1 });
    }
    const bat = kbBatteries.find((b) => b.id === selectedBatteryId);
    if (bat && (bat.selectable || (bat.preisEur != null && bat.preisEur > 0))) {
      extraComponents.push({ name: `Speicher ${bat.hersteller} ${bat.modell} (${bat.kapazitaetKwh} kWh)`, unitPriceEkEur: bat.preisEur ?? 0, ref: `batt:${bat.id}`, quantity: 1 });
    }
    return {
      kwp: num(kwp),
      moduleWp: num(moduleWp),
      ukSystem,
      roofSurfaceCount: Math.max(1, Math.round(num(roofSurfaces))),
      stringCount: Math.max(1, Math.round(num(strings))),
      dcCablePathM: num(dcPath),
      acCablePathM: num(acPath),
      distributorModification: distributorMod,
      meterLoopModification: meterLoop,
      travelDistanceKm: num(travelKm),
      tripCount: Math.max(0, Math.round(num(trips))),
      totalSurchargePct: num(surchargePct),
      craneCostEur: Math.max(0, num(craneCost)),
      fallProtectionCostEur: Math.max(0, num(fallProtectionCost)),
      conduitLengthM: Math.max(0, num(conduitLength)),
      conduitBendCount: Math.max(0, Math.round(num(conduitBends))),
      extraComponents,
      rates: {
        hourlyRateEur: num(hourlyRate) || 72,
        labourEkRateEur: num(labourEkRate) || 40,
        materialMarkupPct: num(materialMarkup),
        labourMarkupPct: num(labourMarkup),
        modulePriceEkEur: num(moduleEk),
      },
    };
  }, [kwp, moduleWp, ukSystem, roofSurfaces, strings, dcPath, acPath,
      distributorMod, meterLoop, travelKm, trips, surchargePct, craneCost,
      fallProtectionCost, conduitLength, conduitBends, hourlyRate,
      labourEkRate, materialMarkup, labourMarkup, moduleEk, extraRows, catalog,
      selectedInverterId, selectedBatteryId, kbInverters, kbBatteries]);

  const handleCalculate = useCallback(async () => {
    setIsCalculating(true);
    setError(null);
    const url = projectId
      ? `/projects/${projectId}/costing/calculate`
      : `/costing/calculate`;
    const res = await apiClient.post<{ result: EngineEnvelope }>(url, buildCalcBody());
    if (res.success) setResult(res.data.result);
    else setError(res.error.message);
    setIsCalculating(false);
  }, [projectId, buildCalcBody]);

  // ── Stage 2: Gespeicherte Kalkulationen ─────────────────────────────────────
  interface SavedCalc {
    id: string; name: string; status: string; version: number;
    updatedAt: string; resultJson?: { data?: { totalNetEur?: number } | null } | null;
  }
  const [savedCalcs, setSavedCalcs] = useState<SavedCalc[]>([]);
  const [calcName, setCalcName] = useState("Kalkulation");
  const [loadedCalcId, setLoadedCalcId] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    if (!projectId) return;
    const res = await apiClient.get<{ items: SavedCalc[] }>(`/projects/${projectId}/costing/calculations`);
    if (res.success) setSavedCalcs(res.data.items);
  }, [projectId]);

  useEffect(() => { void loadList(); }, [loadList]);

  const handleSaveCalc = useCallback(async () => {
    setError(null);
    setSaveMsg(null);
    const body = { name: calcName || "Kalkulation", status: "draft", calculation: buildCalcBody() };
    const res = loadedCalcId
      ? await apiClient.put<SavedCalc>(`/projects/${projectId}/costing/calculations/${loadedCalcId}`, body)
      : await apiClient.post<SavedCalc>(`/projects/${projectId}/costing/calculations`, body);
    if (res.success) {
      setLoadedCalcId(res.data.id);
      setSaveMsg(`Gespeichert ✓ (v${res.data.version}, ${res.data.status})`);
      void loadList();
    } else {
      setError(res.error.message);
    }
  }, [projectId, calcName, loadedCalcId, buildCalcBody, loadList]);

  const handleLoadCalc = useCallback(async (id: string) => {
    setError(null);
    const res = await apiClient.get<{
      id: string; name: string;
      inputJson: Record<string, unknown>; ratesJson: Record<string, unknown>;
      resultJson: EngineEnvelope;
    }>(`/projects/${projectId}/costing/calculations/${id}`);
    if (!res.success) { setError(res.error.message); return; }
    const inp = res.data.inputJson as Record<string, unknown>;
    const rts = res.data.ratesJson as Record<string, unknown>;
    const s = (v: unknown) => (v == null ? "" : String(v));
    setKwp(s(inp.kwp)); setModuleWp(s(inp.moduleWp ?? 475));
    setUkSystem((inp.ukSystem as typeof ukSystem) ?? "two_rail");
    setRoofSurfaces(s(inp.roofSurfaceCount ?? 1)); setStrings(s(inp.stringCount ?? 1));
    setDcPath(s(inp.dcCablePathM ?? 10)); setAcPath(s(inp.acCablePathM ?? 5));
    setDistributorMod(Boolean(inp.distributorModification));
    setMeterLoop(Boolean(inp.meterLoopModification));
    setTravelKm(s(inp.travelDistanceKm ?? 0)); setTrips(s(inp.tripCount ?? 3));
    setSurchargePct(s(inp.totalSurchargePct ?? 0));
    setCraneCost(s(inp.craneCostEur ?? 0));
    setFallProtectionCost(s(inp.fallProtectionCostEur ?? 0));
    setConduitLength(s(inp.conduitLengthM ?? 0));
    setConduitBends(s(inp.conduitBendCount ?? 0));
    setHourlyRate(s(rts.hourlyRateEur ?? 72)); setLabourEkRate(s(rts.labourEkRateEur ?? 40));
    setMaterialMarkup(s(rts.materialMarkupPct ?? 36));
    setLabourMarkup(s(rts.labourMarkupPct ?? 0)); setModuleEk(s(rts.modulePriceEkEur ?? 70));
    // Komponenten-Zeilen rekonstruieren (per Name im Katalog wiederfinden)
    const extras = Array.isArray(inp.extraComponents) ? (inp.extraComponents as Array<{ name: string; quantity: number }>) : [];
    setExtraRows(
      extras.length > 0
        ? extras.map((e) => ({ itemId: catalog.find((c) => c.name === e.name)?.id ?? "", quantity: String(e.quantity) }))
        : [{ itemId: "", quantity: "1" }],
    );
    setInverterTouched(true); setInverterAuto(false); // geladene Kalkulation: keine Auto-Vorwahl drüberlegen
    setResult(res.data.resultJson);
    setCalcName(res.data.name);
    setLoadedCalcId(res.data.id);
    setSaveMsg(`Geladen: ${res.data.name}`);
  }, [projectId, catalog]);

  const handleDuplicate = useCallback(async (id: string) => {
    const res = await apiClient.post<{ id: string }>(`/projects/${projectId}/costing/calculations/${id}/duplicate`, {});
    if (res.success) void loadList();
  }, [projectId, loadList]);

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "5px 8px", fontSize: 12,
    border: "1px solid #d1d5db", borderRadius: 4,
  };
  const badge = (text: string, bg: string, color: string) => (
    <span key={text} style={{ fontSize: 10, fontWeight: 600, background: bg, color, padding: "2px 8px", borderRadius: 999 }}>{text}</span>
  );

  const out = result?.data ?? null;

  return (
    <>
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, background: "#fff" }} data-testid="cost-worksheet">
      {/* Kopf + Sicherheits-Badges (immer sichtbar) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>💶 PV-Kosten-Kalkulation</strong>
        {badge("ENTWURF", "#ebf4ff", "#2563eb")}
        {badge("Interne Kalkulation", "#fffbeb", "#b45309")}
        {badge("Kein Angebot", "#fef2f2", "#b91c1c")}
        {badge("Menschliche Prüfung erforderlich", "#f3f4f6", "#6b7280")}
      </div>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
        Sätze und EK-Preise: Quelle ELMETIC-Preisvergleich 06/2026 — tagesaktuelle EK vor Angebot
        prüfen. Keine Preisgarantie. NICHT enthalten: Netzzutritt/Netzbetreiber-Kosten.
      </div>

      {/* Eingaben */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 10 }}>
        <label style={{ fontSize: 11 }}>Anlagengröße (kWp) *<input data-testid="cost-kwp" type="number" min="0" step="0.1" value={kwp} onChange={(e) => { setKwp(e.target.value); setKwpFromRoof(false); }} style={inputStyle} placeholder="z.B. 10" />
          {kwpFromRoof && <span style={{ fontSize: 10, color: "#15803d", display: "block", marginTop: 2 }}>aus Dachentwurf</span>}
        </label>
        <label style={{ fontSize: 11 }}>Modul aus Katalog
          <select
            value={selectedModuleId}
            onChange={(e) => {
              const id = e.target.value;
              setSelectedModuleId(id);
              if (id) {
                const m = kbModules.find((c) => c.id === id);
                const wp = m?.specs?.nominalPowerWp;
                if (typeof wp === "number") setModuleWp(String(wp));
              }
            }}
            style={inputStyle}
          >
            <option value="">— manuell eingeben —</option>
            {kbModules.filter((m) => m.specs?.nominalPowerWp).map((m) => (
              <option key={m.id} value={m.id}>
                {m.manufacturer} {m.modelName} ({String(m.specs!.nominalPowerWp)} Wp)
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11 }}>Modulleistung (Wp)<input type="number" value={moduleWp} onChange={(e) => { setModuleWp(e.target.value); setSelectedModuleId(""); }} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>UK-System
          <select value={ukSystem} onChange={(e) => setUkSystem(e.target.value as typeof ukSystem)} style={inputStyle}>
            <option value="two_rail">2-Schienen (Standard Ziegeldach)</option>
            <option value="cross_rail">Kreuzverbund (lattenunabhängig)</option>
            <option value="three_rail">3-Schienen (hohe Schneelast)</option>
            <option value="standing_seam">Stehfalz / Blechfalzdach (Klemmmontage)</option>
          </select>
        </label>
        <label style={{ fontSize: 11 }}>Dachflächen<input type="number" min="1" value={roofSurfaces} onChange={(e) => setRoofSurfaces(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Strings<input type="number" min="1" value={strings} onChange={(e) => setStrings(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Wechselrichter {inverterAuto && selectedInverterId ? <span style={{ color: "#15803d", fontWeight: 600 }}>· auto</span> : null}
          <select
            data-testid="cost-inverter"
            value={selectedInverterId}
            onChange={(e) => { setSelectedInverterId(e.target.value); setInverterTouched(true); setInverterAuto(false); }}
            style={inputStyle}
          >
            <option value="">— keiner gewählt —</option>
            {kbInverters.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.hersteller} {inv.modell} · {fmtNum(inv.leistungKw, 1)} kW{!isInstaller && inv.preisEur != null ? ` · ${eur(inv.preisEur)} EK` : ""}
              </option>
            ))}
          </select>
          {inverterAuto && selectedInverterId && (
            <span style={{ fontSize: 10, color: "#15803d", display: "block", marginTop: 2 }}>passend zu {num(kwp)} kWp vorgewählt</span>
          )}
        </label>
        <label style={{ fontSize: 11 }}>Speicher aus Katalog
          <select
            value={selectedBatteryId}
            onChange={(e) => { setSelectedBatteryId(e.target.value); }}
            style={inputStyle}
          >
            <option value="">— kein Speicher —</option>
            {kbBatteries.map((bat) => (
              <option key={bat.id} value={bat.id}>
                {bat.hersteller} {bat.modell} · {fmtNum(bat.kapazitaetKwh, 1)} kWh{!isInstaller && bat.preisEur != null ? ` · ${eur(bat.preisEur)} EK` : ""}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 11 }}>DC-Kabelweg (m, einfach)<input type="number" min="0" value={dcPath} onChange={(e) => setDcPath(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>AC-Kabelweg (m)<input type="number" min="0" value={acPath} onChange={(e) => setAcPath(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Entfernung einfach (km)<input type="number" min="0" value={travelKm} onChange={(e) => setTravelKm(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Anfahrten<input type="number" min="0" value={trips} onChange={(e) => setTrips(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Gesamtaufschlag (%)<input type="number" value={surchargePct} onChange={(e) => setSurchargePct(e.target.value)} style={inputStyle} /></label>
        <label style={{ fontSize: 11 }}>Hebe-/Krankosten (€)<input data-testid="cost-crane" type="number" min="0" value={craneCost} onChange={(e) => setCraneCost(e.target.value)} style={inputStyle} placeholder="0 = ohne" /></label>
        <label style={{ fontSize: 11 }}>Absturzsicherung (€)<input data-testid="cost-fallprotection" type="number" min="0" value={fallProtectionCost} onChange={(e) => setFallProtectionCost(e.target.value)} style={inputStyle} placeholder="PSA/Gerüst" /></label>
        <label style={{ fontSize: 11 }}>Verrohrung Alu 32 (m)<input data-testid="cost-conduit" type="number" min="0" value={conduitLength} onChange={(e) => setConduitLength(e.target.value)} style={inputStyle} placeholder="z.B. 20" /></label>
        <label style={{ fontSize: 11 }}>davon 95°-Bögen (Stk)<input data-testid="cost-conduitbends" type="number" min="0" value={conduitBends} onChange={(e) => setConduitBends(e.target.value)} style={inputStyle} placeholder="z.B. 2" /></label>
        <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, marginTop: 14 }}>
          <input type="checkbox" checked={distributorMod} onChange={(e) => setDistributorMod(e.target.checked)} /> Verteilerumbau
        </label>
        <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, marginTop: 14 }}>
          <input type="checkbox" checked={meterLoop} onChange={(e) => setMeterLoop(e.target.checked)} /> Zählerschleifenanpassung
        </label>
      </div>

      {/* Kalkulationssätze (anpassbar, eingeklappt) — intern, für Installateure verborgen */}
      {!isInstaller && (
      <button onClick={() => setShowRates((v) => !v)} style={{ fontSize: 11, background: "none", border: "none", color: "#2563eb", cursor: "pointer", padding: 0, marginBottom: 8 }}>
        {showRates ? "▾" : "▸"} Kalkulationssätze anpassen (EK/VK-Stundensatz, Aufschläge, Modul-EK)
      </button>
      )}
      {!isInstaller && showRates && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 6, background: "#f9fafb", padding: 8, borderRadius: 6 }}>
          <label style={{ fontSize: 11 }}>EK-Stundensatz (€/h) <span style={{ color: "#6b7280", fontWeight: 400 }}>interne Kosten</span><input type="number" value={labourEkRate} onChange={(e) => setLabourEkRate(e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 11 }}>VK-Stundensatz (€/h) <span style={{ color: "#6b7280", fontWeight: 400 }}>Verrechnungssatz</span><input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 11 }}>Aufschlag Arbeitszeit (%)<input type="number" value={labourMarkup} onChange={(e) => setLabourMarkup(e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 11 }}>Aufschlag Material (%)<input type="number" value={materialMarkup} onChange={(e) => setMaterialMarkup(e.target.value)} style={inputStyle} /></label>
          <label style={{ fontSize: 11 }}>Modul-EK (€/Stk; 0 = ohne)<input type="number" value={moduleEk} onChange={(e) => setModuleEk(e.target.value)} style={inputStyle} /></label>
        </div>
      )}

      {/* Wechselrichter / Speicher / Zubehör */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Wechselrichter / Speicher / Zubehör (aus Preiskatalog)</div>
        {extraRows.map((row, idx) => (
          <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 4 }}>
            <select
              data-testid={`cost-component-${idx}`}
              value={row.itemId}
              onChange={(e) => setExtraRows((rows) => rows.map((r, i) => (i === idx ? { ...r, itemId: e.target.value } : r)))}
              style={{ ...inputStyle, flex: 1 }}
            >
              <option value="">— keine —</option>
              {catalog.filter((c) => c.ekBestEur != null || c.selectable).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{!isInstaller ? ` · ${eur(c.ekBestEur!)} EK` : ""} · {QUALITY_BADGE[c.dataQuality]}
                </option>
              ))}
            </select>
            <input type="number" min="1" value={row.quantity} title="Anzahl"
              onChange={(e) => setExtraRows((rows) => rows.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r)))}
              style={{ ...inputStyle, width: 70 }} />
          </div>
        ))}
        <button onClick={() => setExtraRows((rows) => [...rows, { itemId: "", quantity: "1" }])}
          style={{ fontSize: 11, background: "none", border: "1px dashed #d1d5db", borderRadius: 4, padding: "3px 10px", cursor: "pointer", color: "#6b7280" }}>
          + Zeile
        </button>
      </div>

      <button
        data-testid="cost-calculate"
        onClick={() => void handleCalculate()}
        disabled={isCalculating || !(num(kwp) > 0)}
        className="btn-primary"
        style={{ fontSize: 13, padding: "7px 18px", opacity: isCalculating || !(num(kwp) > 0) ? 0.5 : 1 }}
      >
        {isCalculating ? "Kalkuliere…" : "Kalkulation berechnen"}
      </button>
      {!(num(kwp) > 0) && (
        <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>Anlagengröße (kWp) ist Pflichtfeld.</span>
      )}

      {error && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 8 }}>⚠ {error}</div>}

      {/* Stage 2: Speichern + Varianten (nur im Projekt-Kontext) */}
      {projectId && (
        <>
          <input
            data-testid="calc-name"
            value={calcName}
            onChange={(e) => setCalcName(e.target.value)}
            placeholder="Name der Kalkulation"
            style={{ ...inputStyle, display: "inline-block", width: 200, marginLeft: 12 }}
          />
          <button
            data-testid="calc-save"
            onClick={() => void handleSaveCalc()}
            disabled={!(num(kwp) > 0)}
            style={{ fontSize: 12, padding: "5px 12px", marginLeft: 6, borderRadius: 6, border: "1px solid var(--color-brand, #2563eb)", background: "transparent", color: "var(--color-brand, #2563eb)", cursor: "pointer", opacity: num(kwp) > 0 ? 1 : 0.5 }}
          >
            {loadedCalcId ? "Kalkulation aktualisieren" : "Kalkulation speichern"}
          </button>
          {saveMsg && <span style={{ fontSize: 11, color: "#15803d", marginLeft: 8 }}>{saveMsg}</span>}

          {savedCalcs.length > 0 && (
            <div style={{ marginTop: 12 }} data-testid="saved-calcs">
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Gespeicherte Kalkulationen (Entwürfe — kein Angebot)</div>
              <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "#6b7280", textAlign: "left" }}>
                    <th style={{ padding: 3 }}>Name</th><th style={{ padding: 3 }}>Version</th>
                    <th style={{ padding: 3 }}>Status</th><th style={{ padding: 3 }}>Geändert</th>
                    <th style={{ padding: 3, textAlign: "right" }}>Netto</th><th style={{ padding: 3 }} />
                  </tr>
                </thead>
                <tbody>
                  {savedCalcs.map((c) => (
                    <tr key={c.id} style={{ borderTop: "1px solid #f3f4f6", background: c.id === loadedCalcId ? "#f0f9ff" : undefined }}>
                      <td style={{ padding: 3 }}>{c.name}</td>
                      <td style={{ padding: 3 }}>v{c.version}</td>
                      <td style={{ padding: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 999, background: c.status === "qa_ready" ? "#f0fdf4" : c.status === "needs_review" ? "#fffbeb" : "#ebf4ff", color: c.status === "qa_ready" ? "#15803d" : c.status === "needs_review" ? "#b45309" : "#2563eb" }}>
                          {c.status === "draft" ? "ENTWURF" : c.status === "needs_review" ? "Änderungen nötig" : "fachlich geprüft"}
                        </span>
                      </td>
                      <td style={{ padding: 3 }}>{new Date(c.updatedAt).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td style={{ padding: 3, textAlign: "right" }}>
                        {typeof c.resultJson?.data?.totalNetEur === "number" ? eur(c.resultJson.data.totalNetEur) : "—"}
                      </td>
                      <td style={{ padding: 3, whiteSpace: "nowrap" }}>
                        <button onClick={() => void handleLoadCalc(c.id)} style={{ fontSize: 10, padding: "2px 8px", marginRight: 4, borderRadius: 4, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>Laden</button>
                        <button onClick={() => void handleDuplicate(c.id)} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>Variante</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Ergebnis ── */}
      {result && result.errors.length > 0 && (
        <div style={{ marginTop: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 10, fontSize: 12, color: "#7f1d1d" }}>
          {result.errors.map((e, i) => <div key={i}>⚠ {e.message}</div>)}
        </div>
      )}
      {out && (
        <div style={{ marginTop: 14 }} data-testid="cost-result">
          {/* Druckblatt-Button */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>Kalkulationsergebnis</div>
            <button
              data-testid="cost-print-btn"
              onClick={() => setShowPrintSheet(true)}
              style={{ fontSize: 11, padding: "4px 12px", borderRadius: 6, border: "1px solid #2563eb", background: "#eff6ff", color: "#2563eb", cursor: "pointer" }}
            >
              📄 Als PDF speichern / drucken
            </button>
          </div>
          {/* Mengengerüst */}
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Mengengerüst (automatisch)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6, fontSize: 11, marginBottom: 12 }}>
            <div>Module: <strong>{out.quantities.moduleCount}</strong></div>
            <div>Befestiger: <strong>{out.quantities.fastenerCount}</strong></div>
            <div>Schienen: <strong>{fmtNum(out.quantities.railMeters, 2)} m</strong></div>
            <div>Kleinflächen-Faktor: <strong>{fmtNum(out.quantities.smallRoofFactor, 2)}</strong></div>
            <div>DC-Ader: <strong>{fmtNum(out.quantities.dcWireMeters, 2)} m</strong></div>
            <div>DC-ÜSS: <strong>{out.quantities.dcSurgeCount} Stk</strong></div>
            <div>Erdungsdraht: <strong>{fmtNum(out.quantities.groundingWireMeters, 2)} m</strong></div>
            <div>Arbeitszeit: <strong>{fmtNum(out.quantities.labourHours, 2)} h</strong></div>
            <div>min/Modul: <strong>{fmtNum(out.minutesPerModule, 1)}</strong></div>
            {!isInstaller && out.labourEkEur != null && out.effectiveHourlyRateEur != null && (
              <div>EK-Satz: <strong>{eur(out.labourEkEur / out.quantities.labourHours)}/h</strong> · VK: <strong>{eur(out.effectiveHourlyRateEur)}/h</strong></div>
            )}
          </div>

          {/* Material */}
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Material</div>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", marginBottom: 10 }}>
            <thead><tr style={{ color: "#6b7280", textAlign: "left" }}><th style={{ padding: 3 }}>Position</th>{!isInstaller && <th style={{ padding: 3, textAlign: "right" }}>EK netto</th>}<th style={{ padding: 3, textAlign: "right" }}>{isInstaller ? "Preis netto" : "VK netto"}</th></tr></thead>
            <tbody>
              {out.materialPositions.map((p) => (
                <tr key={p.key} style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td style={{ padding: 3 }}>{p.label}</td>
                  {!isInstaller && <td style={{ padding: 3, textAlign: "right" }}>{p.ekEur != null ? eur(p.ekEur) : "–"}</td>}
                  <td style={{ padding: 3, textAlign: "right" }}>{eur(p.vkEur)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #e5e7eb", fontWeight: 600 }}>
                <td style={{ padding: 3 }}>Material gesamt</td>
                {!isInstaller && <td style={{ padding: 3, textAlign: "right" }}>{out.materialEkEur != null ? eur(out.materialEkEur) : "–"}</td>}
                <td style={{ padding: 3, textAlign: "right" }}>{eur(out.materialVkEur)}</td>
              </tr>
            </tbody>
          </table>

          {/* Arbeit + Pauschalen + Summen */}
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Arbeit &amp; Pauschalen</div>
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", marginBottom: 10 }}>
            <thead><tr style={{ color: "#6b7280", textAlign: "left" }}><th style={{ padding: 3 }}>Position</th>{!isInstaller && <th style={{ padding: 3, textAlign: "right" }}>EK netto</th>}<th style={{ padding: 3, textAlign: "right" }}>{isInstaller ? "Preis netto" : "VK netto"}</th></tr></thead>
            <tbody>
              <tr style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={{ padding: 3 }}>{out.labourPosition.label}</td>
                {!isInstaller && <td style={{ padding: 3, textAlign: "right" }}>{out.labourEkEur != null ? eur(out.labourEkEur) : "–"}</td>}
                <td style={{ padding: 3, textAlign: "right" }}>{eur(out.labourPosition.vkEur)}</td>
              </tr>
              {out.flatPositions.map((p) => (
                <tr key={p.key} style={{ borderTop: "1px solid #f3f4f6" }}>
                  <td style={{ padding: 3 }}>{p.label}</td>
                  {!isInstaller && <td style={{ padding: 3, textAlign: "right", color: "#9ca3af" }}>–</td>}
                  <td style={{ padding: 3, textAlign: "right" }}>{eur(p.vkEur)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #e5e7eb", fontWeight: 600 }}>
                <td style={{ padding: 3 }}>Zwischensumme netto</td>
                {!isInstaller && <td />}
                <td style={{ padding: 3, textAlign: "right" }}>{eur(out.subtotalNetEur)}</td>
              </tr>
              {out.surchargeEur !== 0 && (
                <tr><td style={{ padding: 3 }}>+ Gesamtaufschlag</td>{!isInstaller && <td />}<td style={{ padding: 3, textAlign: "right" }}>{eur(out.surchargeEur)}</td></tr>
              )}
              <tr style={{ fontWeight: 700, borderTop: "2px solid #d1d5db" }}>
                <td style={{ padding: 3 }}>{isInstaller ? "PREIS netto (Richtwert)" : "KALKULATIONSSUMME netto (Entwurf)"}</td>
                {!isInstaller && <td />}
                <td style={{ padding: 3, textAlign: "right" }} data-testid="cost-total-net">{eur(out.totalNetEur)}</td>
              </tr>
              <tr><td style={{ padding: 3 }}>+ 20 % USt</td>{!isInstaller && <td />}<td style={{ padding: 3, textAlign: "right" }}>{eur(out.vatEur)}</td></tr>
              <tr style={{ fontWeight: 700 }}><td style={{ padding: 3 }}>GESAMT brutto{isInstaller ? "" : " (Entwurf)"}</td>{!isInstaller && <td />}<td style={{ padding: 3, textAlign: "right" }}>{eur(out.totalGrossEur)}</td></tr>
            </tbody>
          </table>

          {/* Kennzahlen */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 11, marginBottom: 10, color: "#374151" }}>
            <div>€/kWp netto: <strong>{eur(out.pricePerKwpNetEur)}</strong></div>
            <div>€/Modul netto: <strong>{eur(out.pricePerModuleNetEur)}</strong></div>
            {!isInstaller && out.marginEur != null && out.materialEkEur != null && out.labourEkEur != null && (
            <div>
              Marge gesamt: <strong style={{ color: out.marginEur > 0 ? "#15803d" : "#b91c1c" }}>{eur(out.marginEur)}</strong>
              <span style={{ color: "#6b7280", marginLeft: 4 }}>
                (Material {eur(out.materialVkEur - out.materialEkEur)} + Arbeit {eur(out.labourVkEur - out.labourEkEur)})
              </span>
            </div>
            )}
          </div>

          {/* Warnungen */}
          {result!.warnings.length > 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: 8, fontSize: 11, color: "#92400e", marginBottom: 8 }}>
              {result!.warnings.map((w, i) => <div key={i}>⚠ {w.message}</div>)}
            </div>
          )}

          {/* Annahmen */}
          <button onClick={() => setShowAssumptions((v) => !v)} style={{ fontSize: 11, background: "none", border: "none", color: "#2563eb", cursor: "pointer", padding: 0 }}>
            {showAssumptions ? "▾" : "▸"} Annahmen anzeigen ({result!.assumptions.length})
          </button>
          {showAssumptions && (
            <ul style={{ fontSize: 11, color: "#6b7280", margin: "4px 0 0 16px" }}>
              {result!.assumptions.map((a, i) => <li key={i}>{a.reason}</li>)}
            </ul>
          )}

          <div style={{ marginTop: 10, fontSize: 11, color: "#7f1d1d", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: 8 }}>
            <strong>Interner Kalkulations-Entwurf</strong> — kein rechtsverbindliches Angebot, keine
            Preisgarantie, keine Freigabe (canApprove: false). Prüfung und Angebotserstellung durch
            Fachkraft erforderlich.
          </div>
        </div>
      )}
    </div>
    {showPrintSheet && result && (
      <CostPrintSheet
        result={result}
        kwp={num(kwp)}
        moduleWp={num(moduleWp)}
        ukSystem={ukSystem}
        onClose={() => setShowPrintSheet(false)}
      />
    )}
    </>
  );
}
